import React, { useState, useEffect } from 'react';
import api from '../utils/axiosConfig';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { logout, setGlobalMessage } from '../redux/authSlice';
import { Form, Button, Alert, Card, InputGroup, Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faLock, 
  faCheckCircle, 
  faEnvelope, 
  faUser, 
  faPhone,
  faExclamationCircle,
  faSave,
  faInfoCircle
} from '@fortawesome/free-solid-svg-icons';
import Swal from 'sweetalert2';

const CambioContrasenaPage = () => {
  const [formData, setFormData] = useState({
    password: '',
    passwordConfirm: '',
    emailPersonal: '',
    contactoEmergencia: '',
    telefonoEmergencia: ''
  });
  
  const [errors, setErrors] = useState({
    password: [],
    passwordConfirm: '',
    emailPersonal: '',
    telefonoEmergencia: ''
  });
  
  const [isLoading, setIsLoading] = useState(false);
  const [touched, setTouched] = useState({
    password: false,
    passwordConfirm: false,
    emailPersonal: false,
    telefonoEmergencia: false
  });

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const token = useSelector((state) => state.auth.token);
  const usuario = useSelector((state) => state.auth.usuario);
  const globalMessage = useSelector((state) => state.auth.globalMessage);

  const isValidChileanPhoneNumber = (phoneNumber) => {
    const chileanPhoneRegex = /^\+56\d{9}$/;
    return chileanPhoneRegex.test(phoneNumber);
  };

  const validatePassword = (password) => {
    const errors = [];
    if (!password) return ['La contraseña es requerida'];
    if (password.length < 8) errors.push('Al menos 8 caracteres');
    if (!/[a-z]/.test(password)) errors.push('Una letra minúscula');
    if (!/[A-Z]/.test(password)) errors.push('Una letra mayúscula');
    if (!/[0-9]/.test(password)) errors.push('Un número');
    if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) errors.push('Un carácter especial');
    return errors;
  };

  const validateEmail = (email) => {
    if (!email) return 'El email es requerido';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return 'El email no es válido';
    return '';
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    if (touched[name]) {
      validateField(name, value);
    }
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    if (!touched[name]) {
      setTouched(prev => ({ ...prev, [name]: true }));
    }
    validateField(name, value);
  };

  const validateField = (name, value) => {
    switch (name) {
      case 'password':
        setErrors(prev => ({ ...prev, password: validatePassword(value) }));
        if (touched.passwordConfirm) {
          validateField('passwordConfirm', formData.passwordConfirm);
        }
        break;
      case 'passwordConfirm':
        setErrors(prev => ({ 
          ...prev, 
          passwordConfirm: value !== formData.password ? 'Las contraseñas no coinciden' : '' 
        }));
        break;
      case 'emailPersonal':
        setErrors(prev => ({ ...prev, emailPersonal: validateEmail(value) }));
        break;
      case 'telefonoEmergencia':
        setErrors(prev => ({ 
          ...prev, 
          telefonoEmergencia: value && !isValidChileanPhoneNumber(value) 
            ? 'Formato: +56XXXXXXXXX' 
            : '' 
        }));
        break;
      default:
        break;
    }
  };

  useEffect(() => {
    if (!usuario.email_personal || !usuario.contacto_emergencia || !usuario.telefono_emergencia) {
      dispatch(setGlobalMessage('Por favor, actualiza tus datos personales para continuar.'));
    }
  }, [usuario, dispatch]);

  const handleUpdateDetails = async (e) => {
    e.preventDefault();
    
    // Marcar todos los campos como tocados
    const newTouched = {
      password: true,
      passwordConfirm: true,
      emailPersonal: true,
      telefonoEmergencia: true
    };
    setTouched(newTouched);
    
    // Validar todos los campos
    Object.keys(formData).forEach(name => {
      validateField(name, formData[name]);
    });

    // Verificar si hay errores
    const hasErrors = 
      errors.password.length > 0 || 
      errors.passwordConfirm || 
      errors.emailPersonal || 
      errors.telefonoEmergencia ||
      !formData.password ||
      !formData.passwordConfirm ||
      !formData.emailPersonal ||
      !formData.contactoEmergencia ||
      !formData.telefonoEmergencia;

    if (hasErrors) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Por favor, completa todos los campos correctamente',
        confirmButtonColor: '#d33'
      });
      return;
    }

    setIsLoading(true);
    try {
      await api.post(
        '/auth/update-details',
        {
          newPassword: formData.password,
          emailPersonal: formData.emailPersonal,
          contactoEmergencia: formData.contactoEmergencia,
          telefonoEmergencia: formData.telefonoEmergencia,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      await Swal.fire({
        icon: 'success',
        title: '¡Éxito!',
        text: 'Datos actualizados correctamente. Serás redirigido para iniciar sesión.',
        confirmButtonColor: '#28a745',
        timer: 3000
      });

      dispatch(logout());
      navigate('/login');
    } catch (error) {
      console.error('Error al actualizar los detalles:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error.response?.data?.error || 'Error al actualizar los datos',
        confirmButtonColor: '#d33'
      });
    } finally {
      setIsLoading(false);
    }
  };




return (
  <div className="d-flex justify-content-center mt-5">
    <Card style={{ width: '100%', maxWidth: '500px', padding: '20px' }}>
      <Card.Body>
        {globalMessage && (
          <Alert variant="warning" onClose={() => dispatch(setGlobalMessage(null))} dismissible>
            <FontAwesomeIcon icon={faExclamationCircle} className="me-2" />
            {globalMessage}
          </Alert>
        )}
        
        <h4 className="mb-4 text-center text-primary">
          <FontAwesomeIcon icon={faUser} className="me-2" />
          ¡Hola! Completa tus datos
        </h4>
        
        <Form noValidate onSubmit={handleUpdateDetails}>
          <Form.Group controlId="formPassword" className="mb-3">
            <Form.Label>
              <FontAwesomeIcon icon={faLock} className="me-2" />
              Contraseña Nueva
            </Form.Label>
            <InputGroup hasValidation>
              <InputGroup.Text>
                <FontAwesomeIcon icon={faLock} />
              </InputGroup.Text>
              <Form.Control
                type="password"
                name="password"
                placeholder="Nueva contraseña"
                value={formData.password}
                onChange={handleChange}
                onBlur={handleBlur}
                isInvalid={touched.password && (errors.password.length > 0 || !formData.password)}
                required
              />
              {touched.password && (
                <InputGroup.Text>
                  {errors.password.length === 0 && formData.password ? (
                    <FontAwesomeIcon icon={faCheckCircle} className="text-success" />
                  ) : (
                    <FontAwesomeIcon icon={faExclamationCircle} className="text-danger" />
                  )}
                </InputGroup.Text>
              )}
            </InputGroup>
            <Form.Text className="text-muted">
              Requisitos de contraseña:
            </Form.Text>
            <ul className="small text-muted mt-2">
              {['Al menos 8 caracteres', 'Una letra minúscula', 'Una letra mayúscula', 'Un número', 'Un carácter especial'].map((req, index) => {
                const errorKey = [
                  'La contraseña debe tener al menos 8 caracteres',
                  'Debe incluir al menos una letra minúscula',
                  'Debe incluir al menos una letra mayúscula',
                  'Debe incluir al menos un número',
                  'Debe incluir al menos un carácter especial'
                ][index];
                const isValid = !errors.password.includes(errorKey);
                
                return (
                  <li key={index} className={isValid ? 'text-success' : 'text-danger'}>
                    <FontAwesomeIcon 
                      icon={isValid ? faCheckCircle : faExclamationCircle} 
                      className="me-2" 
                    />
                    {req}
                  </li>
                );
              })}
            </ul>
          </Form.Group>

          <Form.Group controlId="formPasswordConfirm" className="mb-3">
            <Form.Label>
              <FontAwesomeIcon icon={faLock} className="me-2" />
              Confirmar Contraseña
            </Form.Label>
            <InputGroup hasValidation>
              <InputGroup.Text>
                <FontAwesomeIcon icon={faLock} />
              </InputGroup.Text>
              <Form.Control
                type="password"
                name="passwordConfirm"
                placeholder="Confirmar contraseña"
                value={formData.passwordConfirm}
                onChange={handleChange}
                onBlur={handleBlur}
                isInvalid={touched.passwordConfirm && (!!errors.passwordConfirm || !formData.passwordConfirm)}
                required
              />
              {touched.passwordConfirm && (
                <InputGroup.Text>
                  {!errors.passwordConfirm && formData.passwordConfirm ? (
                    <FontAwesomeIcon icon={faCheckCircle} className="text-success" />
                  ) : (
                    <FontAwesomeIcon icon={faExclamationCircle} className="text-danger" />
                  )}
                </InputGroup.Text>
              )}
            </InputGroup>
            <Form.Control.Feedback type="invalid">
              <FontAwesomeIcon icon={faExclamationCircle} className="me-2" />
              {errors.passwordConfirm || 'Este campo es requerido'}
            </Form.Control.Feedback>
          </Form.Group>

          <Form.Group controlId="formEmailPersonal" className="mb-3">
            <Form.Label>
              <FontAwesomeIcon icon={faEnvelope} className="me-2" />
              Email Personal
            </Form.Label>
            <InputGroup hasValidation>
              <InputGroup.Text>
                <FontAwesomeIcon icon={faEnvelope} />
              </InputGroup.Text>
              <Form.Control
                type="email"
                name="emailPersonal"
                placeholder="ejemplo@email.com"
                value={formData.emailPersonal}
                onChange={handleChange}
                onBlur={handleBlur}
                isInvalid={touched.emailPersonal && (!!errors.emailPersonal || !formData.emailPersonal)}
                required
              />
              {touched.emailPersonal && (
                <InputGroup.Text>
                  {!errors.emailPersonal && formData.emailPersonal ? (
                    <FontAwesomeIcon icon={faCheckCircle} className="text-success" />
                  ) : (
                    <FontAwesomeIcon icon={faExclamationCircle} className="text-danger" />
                  )}
                </InputGroup.Text>
              )}
            </InputGroup>
            <Form.Control.Feedback type="invalid">
              <FontAwesomeIcon icon={faExclamationCircle} className="me-2" />
              {errors.emailPersonal || 'Este campo es requerido'}
            </Form.Control.Feedback>
          </Form.Group>

          <Form.Group controlId="formContactoEmergencia" className="mb-3">
            <Form.Label>
              <FontAwesomeIcon icon={faUser} className="me-2" />
              Contacto de Emergencia
            </Form.Label>
            <InputGroup>
              <InputGroup.Text>
                <FontAwesomeIcon icon={faUser} />
              </InputGroup.Text>
              <Form.Control
                type="text"
                name="contactoEmergencia"
                placeholder="Nombre completo"
                value={formData.contactoEmergencia}
                onChange={handleChange}
                required
              />
            </InputGroup>
          </Form.Group>

          <Form.Group controlId="formTelefonoEmergencia" className="mb-3">
            <Form.Label>
              <FontAwesomeIcon icon={faPhone} className="me-2" />
              Teléfono de Emergencia
            </Form.Label>
            <InputGroup hasValidation>
              <InputGroup.Text>
                <FontAwesomeIcon icon={faPhone} />
              </InputGroup.Text>
              <Form.Control
                type="text"
                name="telefonoEmergencia"
                placeholder="+56912345678"
                value={formData.telefonoEmergencia}
                onChange={handleChange}
                onBlur={handleBlur}
                isInvalid={touched.telefonoEmergencia && (!!errors.telefonoEmergencia || !formData.telefonoEmergencia)}
                required
              />
              {touched.telefonoEmergencia && (
                <InputGroup.Text>
                  {!errors.telefonoEmergencia && formData.telefonoEmergencia ? (
                    <FontAwesomeIcon icon={faCheckCircle} className="text-success" />
                  ) : (
                    <FontAwesomeIcon icon={faExclamationCircle} className="text-danger" />
                  )}
                </InputGroup.Text>
              )}
            </InputGroup>
            <Form.Control.Feedback type="invalid">
              <FontAwesomeIcon icon={faExclamationCircle} className="me-2" />
              {errors.telefonoEmergencia || 'Este campo es requerido'}
            </Form.Control.Feedback>
            <Form.Text className="text-muted">
              <FontAwesomeIcon icon={faInfoCircle} className="me-2" />
              Formato: +56 seguido de 9 dígitos (ej: +56912345678)
            </Form.Text>
          </Form.Group>

          <Button 
            type="submit" 
            variant="primary" 
            className="w-100 py-2 fw-bold"
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-2" />
                Procesando...
              </>
            ) : (
              <>
                <FontAwesomeIcon icon={faSave} className="me-2" />
                Guardar Datos y Cambiar Contraseña
              </>
            )}
          </Button>
        </Form>
      </Card.Body>
    </Card>
  </div>
);

}
export default CambioContrasenaPage;